import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';

export const jurisdictionService = {
  selectJurisdiction: ({ data, periodId }) =>
    httpRequest.post('api/report/select-jurisdiction/' + periodId, data),
  updateJurisdiction: ({ id, data }) =>
    httpRequest.put('api/report/update-jurisdiction/' + id, data),
  getJurisdictions: (periodId, params) =>
    httpRequest.get('api/report/get-jurisdictions/' + periodId, { params }),
  getJurisdictionsApprover: (periodId, params) =>
    httpRequest.get('api/report/get-jurisdictions-approver/' + periodId, { params }),
  deleteJurisdiction: id => httpRequest.delete('api/report/delete-jurisdiction/' + id),
  // national code
  createNationalCode: ({ jurisdictionId, data }) =>
    httpRequest.post('api/report/create-national-code/' + jurisdictionId, data),
  getNationalCode: (jurisdictionId, params) =>
    httpRequest.get('api/report/get-national-code/' + jurisdictionId, {
      params
    }),
  updateNationalCode: ({ nationalCodeId, data }) =>
    httpRequest.put('api/report/update-national-code/' + nationalCodeId, data),
  deleteNationalCode: nationalCodeId =>
    httpRequest.put('api/report/delete-national-code/' + nationalCodeId),

  // custom master fund
  getCustomMasterFunds: ({ periodId, workflowId }) =>
    httpRequest.get(`api/report/custom-master-funds/${periodId}`, {
      headers: {
        workflow_id: workflowId
      }
    }),
  createCustomMasterFund: ({ periodId, workflowId, data }) =>
    httpRequest.post(`api/report/create-master-fund/${periodId}`, data, {
      headers: {
        workflow_id: workflowId
      }
    }),
  updateCustomMasterFund: ({ masterFundId, workflowId, data }) =>
    httpRequest.patch(`api/report/custom-master-fund/${masterFundId}`, data, {
      headers: {
        workflow_id: workflowId
      }
    }),
  deleteCustomMasterFund: ({ masterFundId, workflowId }) =>
    httpRequest.delete(`api/report/custom-master-fund/${masterFundId}`, {
      headers: {
        workflow_id: workflowId
      }
    }),
  // countries
  getEuropeCountries: params => httpRequest.get('api/get-countries', { params }),

  // --------------------------- FormPF APIs
  getFormPFJurisdictions: (periodId, params) =>
    httpRequest.get(`api/report/formpf-jurisdiction/${periodId}`, { params }),
  createFormPFJurisdiction: ({ data, periodId, workflowId }) => {
    return httpRequest.post(`api/report/formpf-jurisdiction/${periodId}`, data, {
      headers: {
        workflow_id: workflowId
      }
    });
  },
  updateFormPFJurisdiction: ({ data, periodId, workflowId }) => {
    return httpRequest.put(`api/report/formpf-jurisdiction/${periodId}`, data, {
      headers: {
        workflow_id: workflowId
      }
    });
  }
};

// JURISDICTION

export const useSelectJurisdiction = ({ queryParams = {} } = {}) => {
  return useMutation(jurisdictionService.selectJurisdiction, queryParams);
};

export const useUpdateJurisdiction = ({ queryParams = {} } = {}) => {
  return useMutation(jurisdictionService.updateJurisdiction, queryParams);
};

export const useDeleteJurisdiction = () => {
  return useMutation(jurisdictionService.deleteJurisdiction);
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetJurisdictions = ({ params = {}, queryParams = {}, periodId } = {}) => {
  return useQuery(
    ['GET_JURISDICTIONS', periodId, params],
    () => jurisdictionService.getJurisdictions(periodId, params),
    queryParams
  );
};

export const useGetJurisdictionsApprover = ({ params = {}, queryParams = {}, periodId } = {}) => {
  return useQuery(
    ['GET_JURISDICTIONS_APPROVER', periodId, params],
    () => jurisdictionService.getJurisdictionsApprover(periodId, params),
    queryParams
  );
};

// NATIONAL CODE

export const useCreateNationalCode = ({ queryParams = {} } = {}) => {
  return useMutation(jurisdictionService.createNationalCode, queryParams);
};

export const useUpdateNationalCode = ({ queryParams = {} } = {}) => {
  return useMutation(jurisdictionService.updateNationalCode, queryParams);
};

export const useDeleteNationalCode = () => {
  return useMutation(jurisdictionService.deleteNationalCode);
};

export const useGetNationalCodes = ({ params = {}, queryParams = {}, jurisdictionId } = {}) => {
  return useQuery(
    ['GET_NATIONAL_CODE', jurisdictionId, params],
    () => jurisdictionService.getJurisdictions(jurisdictionId, params),
    queryParams
  );
};

// CUSTOM MASTER FUNDS

export const useGetCustomMasterFunds = ({ queryParams = {}, periodId, workflowId } = {}) => {
  return useQuery(
    ['GET_CUSTOM_MASTER_FUNDS', periodId, workflowId],
    () => jurisdictionService.getCustomMasterFunds({ periodId, workflowId }),
    queryParams
  );
};

export const useCreateCustomMasterFund = ({ queryParams = {}, workflowId, periodId } = {}) => {
  return useMutation(
    data => jurisdictionService.createCustomMasterFund({ periodId, workflowId, data }),
    queryParams
  );
};

export const useUpdateCustomMasterFund = ({ queryParams = {}, workflowId } = {}) => {
  return useMutation(
    ({ masterFundId, data }) =>
      jurisdictionService.updateCustomMasterFund({ masterFundId, workflowId, data }),
    queryParams
  );
};

export const useDeleteCustomMasterFund = ({ queryParams = {}, workflowId } = {}) => {
  return useMutation(
    masterFundId => jurisdictionService.deleteCustomMasterFund({ masterFundId, workflowId }),
    queryParams
  );
};

// COUNTRIES

export const useGetEuropeCountries = ({ queryParams = {} } = {}) => {
  return useQuery(
    ['GET_EUROPE_COUNTRIES'],
    () => jurisdictionService.getEuropeCountries(),
    queryParams
  );
};

// FormPF API endpoints
export const useGetFormPFJurisdiction = ({ params = {}, queryParams = {}, periodId } = {}) => {
  return useQuery(
    ['GET_FORMPF_JURISDICTIONS', periodId, params],
    () => jurisdictionService.getFormPFJurisdictions(periodId, params),
    queryParams
  );
};

export const useCreateFormPFJurisdiction = ({ mutationOptions = {} } = {}) => {
  return useMutation(jurisdictionService.createFormPFJurisdiction, mutationOptions);
};

export const useUpdateFormPFJurisdiction = ({ mutationOptions = {} } = {}) => {
  return useMutation(jurisdictionService.updateFormPFJurisdiction, mutationOptions);
};
